import { type ComponentProps, type MouseEventHandler, useState } from 'react'

import { VaultMediaType } from '@api'

import BrokenImageIcon from '@/assets/icons/icon-broken-image.svg'
import BrokenVideoIcon from '@/assets/icons/icon-broken-video.svg'
import ReloadIcon from '@/assets/icons/icon-reload.svg'
import { classNames } from '@/utils/helpers'

import { Button } from './button'
import { Skeleton } from './skeleton'

interface Props extends ComponentProps<'img'> {
  mediaType?: VaultMediaType
}

export const MediaThumb = ({
  alt = '',
  className,
  mediaType,
  ...props
}: Props) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isError, setIsError] = useState(false)

  const handleLoad = () => {
    setIsLoaded(true)
    setIsError(false)
  }

  const handleError = () => {
    setIsLoaded(false)
    setIsError(true)
  }

  const handleClickRetry: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    setIsLoaded(false)
    setIsError(false)
  }

  return (
    <div className="relative h-full w-full">
      {isError && (
        <div className="flex flex-col justify-center items-center h-full w-full text-secondary-300">
          {mediaType === VaultMediaType.VIDEO ? (
            <BrokenVideoIcon className="h-6 w-6" />
          ) : (
            <BrokenImageIcon className="h-6 w-6" />
          )}
          <p className="text-xs mt-1 mb-2">
            {mediaType === VaultMediaType.VIDEO ? 'Video' : 'Image'} failed to
            load
          </p>
          <Button
            onClick={handleClickRetry}
            size="sm"
          >
            <ReloadIcon />
            Try again
          </Button>
        </div>
      )}
      <img
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={classNames(className, { hidden: !isLoaded || isError })}
        {...props}
      />
      {!isLoaded && !isError && (
        <div className="bg-card inset-0 absolute z-10 rounded-md">
          <Skeleton className="h-full w-full" />
        </div>
      )}
    </div>
  )
}
